import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  Undo2,
  Redo2,
  Plus,
  RotateCcw,
  Settings,
  Database,
  Save,
  Lock,
  Crown,
  Eye,
  Download,
  Share2,
  LayoutGrid,
  Globe,
  RefreshCw,
  XCircle,
  Menu,
  X,
  Monitor,
  Smartphone,
  ToggleLeft,
  ToggleRight,
} from 'lucide-react';
import { EDIT_BREAKPOINT_DESKTOP, EDIT_BREAKPOINT_MOBILE } from '../constants/responsiveBreakpoints';
import TopbarAdvancedMenu from './TopbarAdvancedMenu';
import { readDeliveryMode, setDeliveryMode as persistDeliveryMode } from '../utils/deliveryMode';

const ADVANCED_ORDER = ['global', 'undo', 'redo', 'reset', 'cache', 'gallery', 'siteZip', 'highlightPlan', 'template', 'logout'];
const TOOLBAR_ORDER = ['new', 'save', 'publishMoodle', 'publishSite', 'preview', 'export'];

function ActionButton({ item, compact = false }) {
  const Icon = item.icon;
  const crown = item.showCrown ? <Crown size={10} className="text-amber-400 shrink-0" /> : null;
  const iconOnly = !!item.iconOnly;
  return (
    <button
      type="button"
      className={item.className}
      onClick={item.onClick}
      disabled={item.disabled}
      title={item.title}
      aria-label={item.ariaLabel || item.title || item.label}
      aria-pressed={typeof item.pressed === 'boolean' ? item.pressed : undefined}
    >
      {Icon ? <Icon size={compact || iconOnly ? 16 : 14} className="shrink-0" /> : null}
      {!compact && !iconOnly && item.label ? <span>{item.label}</span> : null}
      {!compact && !iconOnly && crown}
    </button>
  );
}

function MobileMenuRow({ item, onClose }) {
  const Icon = item.icon;
  return (
    <button
      type="button"
      className={`w-full flex items-center gap-3 px-4 py-3 text-left text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${item.mobileClassName || 'text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800'}`}
      onClick={() => {
        if (item.disabled) return;
        item.onClick?.();
        onClose();
      }}
      disabled={item.disabled}
    >
      {Icon ? <Icon size={18} className="shrink-0 opacity-80" /> : null}
      <span className="flex-1">{item.label}</span>
      {item.showCrown ? <Crown size={12} className="text-amber-400 shrink-0" /> : null}
    </button>
  );
}

export default function BuilderTopbarActions({
  appState,
  isCanvasReadOnly,
  historyState,
  hasStageContent,
  currentUser,
  canSave,
  onUndo,
  onRedo,
  onNewProject,
  onResetStage,
  onGlobalSettings,
  onClearCache,
  onSave,
  onPublishSite,
  isPublishingSite = false,
  onPublishMoodle,
  isPublishingMoodle = false,
  onPreview,
  onExport,
  onExportSite,
  onGallery,
  onSaveTemplate,
  onLogout,
  editBreakpoint = EDIT_BREAKPOINT_DESKTOP,
  onEditBreakpointChange,
  highlightPlanRestricted = false,
  onToggleHighlightPlanRestricted = null,
  showHighlightPlanRestricted = false,
  canHighlightPlanRestricted = false,
}) {
  const { t } = useTranslation(['builder', 'common']);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [deliveryMode, setDeliveryMode] = useState(readDeliveryMode);

  const chooseDeliveryMode = useCallback((mode) => {
    setDeliveryMode(persistDeliveryMode(mode));
  }, []);

  const closeMobile = useCallback(() => setMobileOpen(false), []);

  useEffect(() => {
    if (!mobileOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') closeMobile();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mobileOpen, closeMobile]);

  const actionItems = useMemo(() => {
    const items = [];

    if (appState === 'builder' && !isCanvasReadOnly) {
      items.push({
        id: 'undo',
        label: t('builder:undo'),
        title: `${t('builder:undo')} (Ctrl+Z)`,
        icon: Undo2,
        onClick: onUndo,
        disabled: !historyState?.canUndo,
        className:
          'flex items-center gap-1 px-2 py-1.5 text-xs font-medium rounded-md transition-colors disabled:opacity-40 disabled:cursor-not-allowed text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700',
        mobileClassName: 'text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800',
        group: 'history',
      });
      items.push({
        id: 'redo',
        label: t('builder:redo'),
        title: `${t('builder:redo')} (Ctrl+Shift+Z)`,
        icon: Redo2,
        onClick: onRedo,
        disabled: !historyState?.canRedo,
        className:
          'flex items-center gap-1 px-2 py-1.5 text-xs font-medium rounded-md transition-colors disabled:opacity-40 disabled:cursor-not-allowed text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700',
        mobileClassName: 'text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800',
        group: 'history',
      });
    }

    if (!isCanvasReadOnly) {
      items.push({
        id: 'new',
        label: t('builder:newProjectShort', { defaultValue: 'Novo' }),
        title: t('builder:newProject'),
        icon: Plus,
        onClick: onNewProject,
        className:
          'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50 rounded-md transition-colors border border-blue-200 dark:border-blue-800',
        mobileClassName: 'text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/30',
        group: 'project',
      });
    }

    if (!isCanvasReadOnly && hasStageContent) {
      items.push({
        id: 'reset',
        label: t('builder:reset'),
        title: t('builder:resetTitle'),
        icon: RotateCcw,
        onClick: onResetStage,
        className:
          'flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors border border-slate-200 hover:border-red-200',
        mobileClassName: 'text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20',
        group: 'project',
      });
    }

    if (!isCanvasReadOnly) {
      items.push({
        id: 'global',
        label: t('builder:global'),
        icon: Settings,
        onClick: onGlobalSettings,
        className:
          'flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-md transition-colors',
        group: 'settings',
      });
      items.push({
        id: 'cache',
        label: t('builder:clearBrowserCacheButton'),
        title: t('builder:clearBrowserCacheTitle'),
        icon: Database,
        onClick: onClearCache,
        className:
          'flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-amber-800 dark:text-amber-200 bg-amber-50 dark:bg-amber-950/40 hover:bg-amber-100 dark:hover:bg-amber-900/50 rounded-md transition-colors border border-amber-200 dark:border-amber-800',
        mobileClassName: 'text-amber-800 dark:text-amber-200 hover:bg-amber-50 dark:hover:bg-amber-950/40',
        group: 'settings',
      });
      items.push({
        id: 'save',
        label: t('builder:save'),
        title: canSave ? t('builder:saveTitle') : t('builder:saveDisabled'),
        icon: canSave ? Save : Lock,
        onClick: onSave,
        showCrown: true,
        className: `flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
          !canSave
            ? 'text-slate-400 hover:bg-amber-50 border border-dashed border-slate-300'
            : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
        }`,
        group: 'save',
      });
      if (typeof onPublishMoodle === 'function') {
        items.push({
          id: 'publishMoodle',
          label: isPublishingMoodle ? t('builder:coursePublishing') : t('builder:coursePublishButton'),
          title: t('builder:coursePublishTitle'),
          icon: RefreshCw,
          onClick: onPublishMoodle,
          disabled: isPublishingMoodle,
          className:
            'flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-white bg-blue-700 hover:bg-blue-800 rounded-md transition-colors shadow-sm border border-blue-800/20 disabled:opacity-60 whitespace-nowrap',
          mobileClassName: 'text-blue-700 dark:text-blue-200 hover:bg-blue-50 dark:hover:bg-blue-900/30',
          group: 'save',
        });
      }
      if (typeof onPublishSite === 'function') {
        items.push({
          id: 'publishSite',
          label: isPublishingSite ? t('builder:sitePublishing') : t('builder:sitePublishButton'),
          title: t('builder:sitePublishTitle'),
          icon: Globe,
          onClick: onPublishSite,
          disabled: isPublishingSite,
          className:
            'flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md transition-colors shadow-sm border border-indigo-700/20 disabled:opacity-60 whitespace-nowrap',
          mobileClassName: 'text-indigo-700 dark:text-indigo-200 hover:bg-indigo-50 dark:hover:bg-indigo-900/30',
          group: 'save',
        });
      }
    }

    items.push({
      id: 'preview',
      label: t('builder:preview'),
      icon: Eye,
      onClick: onPreview,
      className:
        'flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-emerald-600 bg-emerald-50 hover:bg-emerald-100 dark:bg-emerald-900/30 dark:hover:bg-emerald-900/50 rounded-md transition-colors border border-emerald-200 dark:border-emerald-800',
      mobileClassName: 'text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-900/30',
      group: 'publish',
    });

    if (!isCanvasReadOnly) {
      items.push({
        id: 'export',
        label: t('builder:exportZipButton'),
        title: deliveryMode === 'site'
          ? t('builder:siteExportMenuTitle', { defaultValue: 'Baixa o HTML inicial do site. Depois, use Publicar no site para atualizar.' })
          : t('builder:exportZipMoodleOnlyTitle', { defaultValue: 'Exportar ZIP somente para o Moodle' }),
        icon: Download,
        onClick: deliveryMode === 'site' && typeof onExportSite === 'function' ? onExportSite : onExport,
        className:
          'flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-500 rounded-md transition-colors shadow-sm border border-blue-700/20',
        mobileClassName: 'text-blue-700 dark:text-blue-200 hover:bg-blue-50 dark:hover:bg-blue-900/30',
        group: 'publish',
      });
    }

    if (
      showHighlightPlanRestricted
      && typeof onToggleHighlightPlanRestricted === 'function'
      && (canHighlightPlanRestricted || highlightPlanRestricted)
    ) {
      items.push({
        id: 'highlightPlan',
        label: t('builder:highlightPlanRestrictedToggle'),
        title: t('builder:highlightPlanRestrictedTitle'),
        ariaLabel: t('builder:highlightPlanRestrictedTitle'),
        icon: highlightPlanRestricted ? ToggleRight : ToggleLeft,
        iconOnly: true,
        pressed: highlightPlanRestricted,
        onClick: onToggleHighlightPlanRestricted,
        className: `flex items-center justify-center p-2 rounded-md transition-colors border ${
          highlightPlanRestricted
            ? 'text-rose-600 dark:text-rose-400 bg-rose-50 dark:bg-rose-950/40 border-rose-300 dark:border-rose-700'
            : 'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 border-slate-200 dark:border-slate-600'
        }`,
        mobileClassName: highlightPlanRestricted
          ? 'text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/40'
          : 'text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800',
        group: 'publish',
      });
    }

    if (!isCanvasReadOnly && currentUser?.tenant_id && hasStageContent) {
      items.push({
        id: 'gallery',
        label: t('builder:gallery'),
        title: t('builder:shareToGallery'),
        icon: Share2,
        onClick: onGallery,
        className:
          'flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/30 dark:hover:bg-blue-900/50 rounded-md transition-colors border border-blue-200 dark:border-blue-800',
        mobileClassName: 'text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/30',
        group: 'publish',
      });
    }

    if (!isCanvasReadOnly && currentUser?.role === 'super_admin' && hasStageContent) {
      items.push({
        id: 'template',
        label: t('builder:template'),
        title: t('builder:saveAsTemplate'),
        icon: LayoutGrid,
        onClick: onSaveTemplate,
        className:
          'flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-amber-600 bg-amber-50 hover:bg-amber-100 dark:bg-amber-900/30 dark:hover:bg-amber-900/50 rounded-md transition-colors border border-amber-200 dark:border-amber-800',
        mobileClassName: 'text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/30',
        group: 'publish',
      });
    }

    items.push({
      id: 'logout',
      label: t('common:logout'),
      title: t('common:logout'),
      icon: XCircle,
      onClick: onLogout,
      className:
        'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-md transition-all',
      mobileClassName: 'text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20',
      group: 'account',
    });

    return items;
  }, [
    appState,
    isCanvasReadOnly,
    historyState,
    hasStageContent,
    currentUser,
    canSave,
    t,
    onUndo,
    onRedo,
    onNewProject,
    onResetStage,
    onGlobalSettings,
    onClearCache,
    onSave,
    onPublishSite,
    isPublishingSite,
    onPublishMoodle,
    isPublishingMoodle,
    deliveryMode,
    onPreview,
    onExport,
    onExportSite,
    onGallery,
    onSaveTemplate,
    onLogout,
    highlightPlanRestricted,
    onToggleHighlightPlanRestricted,
    showHighlightPlanRestricted,
    canHighlightPlanRestricted,
  ]);

  const previewItem = actionItems.find((x) => x.id === 'preview');
  const menuItems = actionItems.filter((x) => x.id !== 'preview');
  const byId = new Map(actionItems.map((item) => [item.id, item]));
  const toolbarItems = TOOLBAR_ORDER.map((id) => byId.get(id)).filter(Boolean);
  const advancedItems = ADVANCED_ORDER.map((id) => byId.get(id)).filter(Boolean);

  const deliverySwitch = (
    <div
      className="flex items-center gap-0.5 p-0.5 rounded-lg bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-600 flex-shrink-0"
      role="group"
      aria-label={t('builder:deliveryModeLabel', { defaultValue: 'Modo do projeto' })}
    >
      <button
        type="button"
        className={`px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors ${deliveryMode === 'course' ? 'bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 shadow-sm' : 'text-slate-500 dark:text-slate-400 hover:text-slate-700'}`}
        onClick={() => chooseDeliveryMode('course')}
        aria-pressed={deliveryMode === 'course'}
        title={t('builder:deliveryModeCourseTitle', { defaultValue: 'Barra para curso no Moodle' })}
      >
        {t('builder:deliveryModeCourse', { defaultValue: 'Curso' })}
      </button>
      <button
        type="button"
        className={`px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors ${deliveryMode === 'site' ? 'bg-white dark:bg-slate-700 text-indigo-700 dark:text-indigo-300 shadow-sm' : 'text-slate-500 dark:text-slate-400 hover:text-slate-700'}`}
        onClick={() => chooseDeliveryMode('site')}
        aria-pressed={deliveryMode === 'site'}
        title={t('builder:deliveryModeSiteTitle', { defaultValue: 'Barra para publicar a página no site' })}
      >
        {t('builder:deliveryModeSite', { defaultValue: 'Site' })}
      </button>
    </div>
  );

  const renderDesktopToolbar = () => (
    <>
      {toolbarItems.map((item, idx) => {
        const prev = toolbarItems[idx - 1];
        const showSep =
          prev &&
          ((prev.group === 'history' && item.group === 'project') ||
            (prev.group === 'save' && item.group === 'publish'));
        const isReveal = item.id === 'publishSite' || item.id === 'publishMoodle';
        const revealOpen = item.id === 'publishSite'
          ? deliveryMode === 'site'
          : item.id === 'publishMoodle'
            ? deliveryMode === 'course'
            : false;
        const button = isReveal ? (
          <div
            className={`geppetto-topbar-reveal${revealOpen ? ' is-open' : ''}`}
            aria-hidden={!revealOpen}
          >
            <div className="geppetto-topbar-reveal-inner">
              <ActionButton item={{ ...item, disabled: item.disabled || !revealOpen }} />
            </div>
          </div>
        ) : (
          <ActionButton item={item} />
        );
        return (
          <React.Fragment key={item.id}>
            {showSep ? <span className="w-px h-5 bg-slate-200 dark:bg-slate-600 mx-0.5" /> : null}
            {button}
          </React.Fragment>
        );
      })}
      <TopbarAdvancedMenu items={advancedItems} />
    </>
  );

  const mobileMenuPortal = mobileOpen
    ? createPortal(
        <div className="fixed inset-0 z-[100040] md:hidden" role="dialog" aria-modal="true" aria-label={t('builder:topbarMenuTitle', { defaultValue: 'Ações do projeto' })}>
          <button
            type="button"
            className="absolute inset-0 bg-slate-900/50 border-0 p-0 cursor-pointer"
            aria-label={t('builder:close', { defaultValue: 'Fechar' })}
            onClick={closeMobile}
          />
          <div className="absolute right-0 top-0 bottom-0 w-[min(100%,18.5rem)] bg-white dark:bg-slate-900 shadow-2xl border-l border-slate-200 dark:border-slate-700 flex flex-col animate-in slide-in-from-right-4 duration-200">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-700 shrink-0">
              <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                {t('builder:topbarMenuTitle', { defaultValue: 'Ações do projeto' })}
              </h2>
              <button
                type="button"
                onClick={closeMobile}
                className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
                aria-label={t('builder:close', { defaultValue: 'Fechar' })}
              >
                <X size={18} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto py-2">
              <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-800">
                {deliverySwitch}
              </div>
              {menuItems.map((item, idx) => {
                const prev = menuItems[idx - 1];
                const showSep = prev && prev.group !== item.group && item.id !== 'logout';
                return (
                  <React.Fragment key={item.id}>
                    {showSep ? <div className="my-2 border-t border-slate-100 dark:border-slate-800" /> : null}
                    {item.id === 'logout' ? <div className="my-2 border-t border-slate-100 dark:border-slate-800" /> : null}
                    <MobileMenuRow item={item} onClose={closeMobile} />
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <>
      <div className="hidden md:flex items-center gap-4 flex-shrink-0 min-w-0">
      {/* Alternador Desktop / Mobile (palco) */}
      {appState === 'builder' && onEditBreakpointChange ? (
        <div
          className="flex items-center gap-0.5 p-0.5 rounded-lg bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-600 flex-shrink-0"
          role="group"
          aria-label={t('builder:editBreakpointLabel', { defaultValue: 'Modo de edição do palco' })}
        >
          <button
            type="button"
            className={`flex items-center gap-1 px-2 py-1.5 text-xs font-medium rounded-md transition-colors ${editBreakpoint === EDIT_BREAKPOINT_DESKTOP ? 'bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 shadow-sm' : 'text-slate-500 dark:text-slate-400 hover:text-slate-700'}`}
            onClick={() => onEditBreakpointChange(EDIT_BREAKPOINT_DESKTOP)}
            title={t('builder:editBreakpointDesktopTitle', { defaultValue: 'Editar layout desktop' })}
            aria-pressed={editBreakpoint === EDIT_BREAKPOINT_DESKTOP}
          >
            <Monitor size={15} aria-hidden />
            <span className="hidden lg:inline">{t('builder:previewDeviceDesktop')}</span>
          </button>
          <button
            type="button"
            className={`flex items-center gap-1 px-2 py-1.5 text-xs font-medium rounded-md transition-colors ${editBreakpoint === EDIT_BREAKPOINT_MOBILE ? 'bg-white dark:bg-slate-700 text-emerald-700 dark:text-emerald-300 shadow-sm' : 'text-slate-500 dark:text-slate-400 hover:text-slate-700'}`}
            onClick={() => onEditBreakpointChange(EDIT_BREAKPOINT_MOBILE)}
            title={t('builder:editBreakpointMobileTitle', { defaultValue: 'Editar layout mobile' })}
            aria-pressed={editBreakpoint === EDIT_BREAKPOINT_MOBILE}
          >
            <Smartphone size={15} aria-hidden />
            <span className="hidden lg:inline">{t('builder:previewDeviceMobile')}</span>
          </button>
        </div>
      ) : null}

      {appState === 'builder' ? deliverySwitch : null}

      {/* Desktop: barra completa sem rolagem horizontal */}
      <div className="flex items-center gap-2 flex-shrink-0 min-w-0">
        {renderDesktopToolbar()}
      </div>
      </div>

      {/* Mobile: atalho visualizar + modo palco + menu lateral */}
      <div className="flex md:hidden items-center gap-1 flex-shrink-0">
        {onEditBreakpointChange ? (
          <div className="flex items-center gap-0.5 p-0.5 rounded-md bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-600">
            <button
              type="button"
              className={`p-1.5 rounded ${editBreakpoint === EDIT_BREAKPOINT_DESKTOP ? 'bg-white dark:bg-slate-700 shadow-sm' : ''}`}
              onClick={() => onEditBreakpointChange(EDIT_BREAKPOINT_DESKTOP)}
              aria-pressed={editBreakpoint === EDIT_BREAKPOINT_DESKTOP}
              title={t('builder:editBreakpointDesktopTitle', { defaultValue: 'Desktop' })}
            >
              <Monitor size={15} />
            </button>
            <button
              type="button"
              className={`p-1.5 rounded ${editBreakpoint === EDIT_BREAKPOINT_MOBILE ? 'bg-white dark:bg-slate-700 shadow-sm' : ''}`}
              onClick={() => onEditBreakpointChange(EDIT_BREAKPOINT_MOBILE)}
              aria-pressed={editBreakpoint === EDIT_BREAKPOINT_MOBILE}
              title={t('builder:editBreakpointMobileTitle', { defaultValue: 'Mobile' })}
            >
              <Smartphone size={15} />
            </button>
          </div>
        ) : null}
        {previewItem ? (
          <button
            type="button"
            className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-emerald-600 bg-emerald-50 dark:bg-emerald-900/30 rounded-md border border-emerald-200 dark:border-emerald-800"
            onClick={previewItem.onClick}
            title={previewItem.label}
          >
            <Eye size={15} />
            <span className="sr-only">{previewItem.label}</span>
          </button>
        ) : null}
        <button
          type="button"
          className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-md transition-colors"
          onClick={() => setMobileOpen(true)}
          aria-expanded={mobileOpen}
          aria-haspopup="dialog"
          title={t('builder:topbarMenuOpen', { defaultValue: 'Menu' })}
        >
          <Menu size={16} />
          <span>{t('builder:topbarMenuOpen', { defaultValue: 'Menu' })}</span>
        </button>
      </div>

      {mobileMenuPortal}
    </>
  );
}
